import { Alert, AlertDescription, AlertIcon, Box, Button, Flex } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useGetNegotiationRoundQuery } from "../../../../autogen/bff-api";
import { CombinedDocuments } from "../../../../common/documents/CombinedDocuments";
import { IncompleteFileUpload, isIncompleteFileUpload } from "../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../common/documents/UppyUploader/useUppyUploader";

interface Props {
  negotiationId: string;
  nextStep: () => void;
  previousStep: () => void;
}

export const Docs = ({ negotiationId, nextStep, previousStep }: Props) => {
  const { t } = useTranslation();

  const { data: negotiationRound, refetch } = useGetNegotiationRoundQuery(
    negotiationId ? { negotiationId } : skipToken
  );

  const { uppy, allFiles, setDocuments, documents, setUploads, removeUpload } = useUppyUploader({
    entityId: negotiationId,
    entityType: "NegotiationRound",
    reloadEntity: refetch,
  });

  useEffect(() => {
    setDocuments(negotiationRound?.documents ?? []);
  }, [negotiationRound?.documents, setDocuments]);

  useEffect(() => {
    setUploads(negotiationRound?.uploads ?? []);
  }, [negotiationRound?.uploads, setUploads]);

  return (
    <>
      {negotiationRound?.stateName === "Published" && (
        <Alert status="info" rounded="lg" mb="8">
          <AlertIcon />
          <Box>
            <AlertDescription>{t("Documents you upload will be visible to invited suppliers")}</AlertDescription>
          </Box>
        </Alert>
      )}
      <CombinedDocuments
        uploadedDocuments={documents}
        setUploadedDocuments={setDocuments}
        documentUploadConfig={{
          uppy,
          dropTargetId: "negotiationRoundFileTarget",
          dropTargetHelperText: t("Upload documents for this negotiation round"),
          uploads: allFiles.filter((f) => isIncompleteFileUpload(f)) as IncompleteFileUpload[],
          removeUpload,
        }}
        canEditTextDocuments={false}
        canDeleteDocuments={true}
        canEditDocumentNames={true}
        isTagsColumnVisible={true}
        canEditTags={true}
      />
      <Flex py="10">
        <Button variant={"outline"} mr="5px" w="100%" colorScheme={"teal"} onClick={previousStep}>
          {t("Previous")}
        </Button>
        <Button variant={"solid"} w="100%" colorScheme={"teal"} onClick={nextStep}>
          {t("Next")}
        </Button>
      </Flex>
    </>
  );
};
